/**
 * Holds the device fingerprint collected at app bootstrap. Synchronous
 * access after `collect()` completes is what allows auth flows (signup,
 * Guest start) to attach `fingerprint_hash` to their requests.
 *
 * On collection failure (privacy-protected browser, etc.) the store still
 * exposes a degraded hash so the request can proceed; the server then
 * applies its `ANTIBYPASS_FAIL_MODE` policy.
 */

import { create } from 'zustand';
import type { FingerprintComponents } from '@shared/device-fingerprint';
import { collectDeviceFingerprint } from '../lib/deviceFingerprint';
import { useAuthStore } from './useAuthStore';

type Status = 'idle' | 'collecting' | 'submitted' | 'error';

interface State {
  hash_hex: string | null;
  components: FingerprintComponents | null;
  status: Status;
  error: string | null;
  collect: () => Promise<void>;
  reset: () => void;
}

export const useFingerprintStore = create<State>((set, get) => ({
  hash_hex: null,
  components: null,
  status: 'idle',
  error: null,

  collect: async () => {
    if (get().status === 'collecting' || get().status === 'submitted') return;
    set({ status: 'collecting', error: null });
    try {
      const { hash_hex, components } = await collectDeviceFingerprint();
      set({ hash_hex, components, status: 'submitted', error: null });

      // Fire-and-forget POST to server. We intentionally don't await: collection
      // is the load-bearing step for auth flows; submission to server is best-effort.
      try {
        // If authenticated, wait briefly for csrfToken to hydrate (else CSRF
        // middleware will 403 the POST). Anonymous users skip this — server
        // CSRF check is bypassed when sessionState !== 'valid'.
        const csrf = await waitForCsrfTokenIfAuthenticated();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (csrf) headers['X-CSRF-Token'] = csrf;
        await fetch('/api/v1/device-fingerprint', {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ hash_hex, components }),
        });
      } catch {
        // swallow — collection succeeded, server submit failed, auth still works
      }
    } catch (err) {
      set({
        hash_hex: null,
        components: null,
        status: 'error',
        error: err instanceof Error ? err.message : 'unknown',
      });
    }
  },

  reset: () => set({ hash_hex: null, components: null, status: 'idle', error: null }),
}));

/**
 * Wait for auth state to settle (loading → authenticated|unauthenticated). If
 * authenticated, wait for `csrfToken` to populate too. Total cap 3s.
 *
 * Fixes the B06a race: at App-mount, fingerprint POST can fire while
 * `useAuthStore.authState === 'loading'` (the `/auth/me` request hasn't
 * resolved yet). Without this wait, returning users with a valid session
 * cookie see a 403 because the server requires CSRF but the frontend hasn't
 * hydrated the token yet.
 */
async function waitForCsrfTokenIfAuthenticated(): Promise<string> {
  const maxAttempts = 30;
  const stepMs = 100;

  for (let i = 0; i < maxAttempts; i++) {
    const auth = useAuthStore.getState();

    // Anonymous user — no CSRF needed; server bypasses CSRF middleware for
    // unauthenticated sessions.
    if (auth.authState === 'unauthenticated') return '';

    // Authenticated AND CSRF token populated — ready to POST.
    if (auth.authState === 'authenticated' && auth.csrfToken !== '') {
      return auth.csrfToken;
    }

    // Either still 'loading', or 'authenticated' but csrfToken not yet set.
    await new Promise<void>((resolve) => setTimeout(resolve, stepMs));
  }

  // Timed out — fall back to whatever token (likely empty) is available.
  // The caller's try/catch swallows the resulting 403 silently.
  return useAuthStore.getState().csrfToken;
}
